import React, { useState } from 'react';

import AutoSizer from 'react-virtualized-auto-sizer';

import { LogIcon } from '@primer/octicons-react';
import { StyledOcticon } from '@primer/react';

import Button from '../common/Button/Button';
import CardView from '../common/CardView/CardView';
import ErrorLabel from '../common/Labels/ErrorLabel';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';
import LogJsonView from './LogJsonView';
import LogSelectionList from './LogSelectionList';
import ModalOverlay from '../common/ModalOverlay/ModalOverlay';

import { PipelineLogsProvider, usePipelineLogsContext } from './PipelineLogsContext';
import { AuditLogEntryType } from '../../services/logs_service';

interface LogsIndexProps {
  logs: AuditLogEntryType[],
  isLoading: boolean,
  hasError: boolean,
  nextPageToken: number | null,
  handleLoadNextPage: (startIndex: number, stopIndex: number) => void | Promise<void>,
}

export const LogsIndex = ({
  logs = [],
  isLoading = false,
  hasError = false,
  nextPageToken = null,
  handleLoadNextPage = () => {},
}: LogsIndexProps) => {
  const [selectedLog, setSelectedLog] = useState<AuditLogEntryType | null>(null);

  const showNoDataMessage = logs.length === 0;
  const message = isLoading ? 'Loading\u2026' : showNoDataMessage ? 'No data in the selected time range' : 'There was an error loading logs for this time range';

  const onModalToggle = (isActive: boolean) => {
    if (!isActive) {
      setSelectedLog(null)
    }
  };

  return (hasError || showNoDataMessage || isLoading) ? (
    <div className="p-4 relative h-full flex flex-col justify-center items-center">
      { isLoading ? <LoadingSpinner animationKey="audit-logs-loading-spinner" useOverlay /> : null }
      <StyledOcticon
        className="mb-4 text-brand"
        icon={LogIcon}
        size={64}
      />
      <ErrorLabel className="flex justify-center items-center" size="2xl">
        {message}
      </ErrorLabel>
    </div>
  ) : (
    <div className="mt-2 flex flex-col items-stretch h-80">
      <div className="flex flex-row justify-between">
        <div className="flex-grow-1 w-full h-80">
          <AutoSizer>
            {
              ({ height, width }) => (
                <LogSelectionList
                  height={height}
                  width={width}
                  logs={logs}
                  selectedTimestamp={selectedLog?.unixTime}
                  onLogSelected={setSelectedLog}
                  handleLoadNextPage={handleLoadNextPage}
                  nextPageToken={nextPageToken}
                />
              )
            }
          </AutoSizer>
        </div>
        {
          selectedLog ? (
            <ModalOverlay
              animationKey="log-modal"
              active={!!selectedLog}
              onModalToggle={onModalToggle}
            >
              <div id="logModal" className="h-2/3 mx-4">
                <CardView className="relative">
                  <Button
                    variant="close"
                    className="p-2 absolute right-0 top-0"
                    textColor="border-darkest"
                    activeTextColor="border-dark"
                    onClick={() => setSelectedLog(null)}
                  />
                  <LogJsonView data={selectedLog?.logData} />
                </CardView>
              </div>
            </ModalOverlay>
          ) : null
        }
      </div>
    </div>
  );
};

const ContextualPipelineLogsIndex = () => {
  const {
    logs,
    isLoading,
    hasError,
    nextPageToken,
    handleLoadNextPage,
  } = usePipelineLogsContext();

  return (
    <LogsIndex
      logs={logs}
      isLoading={isLoading}
      hasError={hasError}
      nextPageToken={nextPageToken}
      handleLoadNextPage={handleLoadNextPage}
    />
  )
}

type PipelineLogsIndexProps = {
  pipelineID: string,
  isHidden: boolean,
}

const PipelineLogsIndex = ({ pipelineID, isHidden = false }: PipelineLogsIndexProps) => {
  if (isHidden) {
    return null;
  }
  return (
    <PipelineLogsProvider pipelineID={pipelineID}>
      <ContextualPipelineLogsIndex />
    </PipelineLogsProvider>
  )
}

export default PipelineLogsIndex;
